/**
 * QuestLogScene (key 'Quests') — the warm, pressure-free quest journal (owner decision
 * D17). Two tabs: Active (in-progress) and Done (finished tales). Each row shows the
 * quest title, its giver, a one-line synopsis, and — for active quests — a SOFT progress
 * line per objective ("Caught 2 of 3 bug Pokémon"). Main-story quests are pinned above
 * side quests. Tapping an active row shows its current stage's opening lines.
 *
 * NEVER a nag: no counters in red, no "overdue", no deadline, no X/Y completion score.
 * A tired player who never opens this still plays a complete game. Touch-first + full
 * keyboard + phone-portrait reachable + re-entry safe (Phaser reuses the instance), the
 * codebase conventions. All truth comes from the GameSession; the renderer owns no rules.
 */
import Phaser from 'phaser';
import {
  emptyQuestState,
  type QuestDef,
  type QuestEvent,
  type QuestProgress,
  type QuestState,
} from '@/shared';
import type { GameSession } from '@/session';
import { GYM_LEADERS } from '@/data/quests';
import { COLORS, CSS, FONT, RADIUS, SPACING } from './ui/theme.js';
import { Panel } from './ui/Panel.js';
import { Button } from './ui/Button.js';
import { DialogBox } from './ui/DialogBox.js';
import { ScrollList } from './ui/ScrollList.js';
import { FocusRing } from './ui/FocusRing.js';
import { domTextFieldFocused } from './ui/keyboardGate.js';
import { Layout } from './layout/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { getSession } from './session.js';
import { calmBackdrop, redrawBackdrop } from './collection/parts.js';
import { activeStageLines, humanizeId, isReadyToTurnIn } from './questLogFormat.js';

// ── Shared quest-session adapter ─────────────────────────────────────────────────────
//
// The quest surface GameSession exposes (added by the persistence workstream). The quest
// scenes read/mutate through questView() so they BUILD and RENDER a warm empty state even
// before that lands, and pick up the real state the moment it does. Call sites use the
// exact contract names (questState / questDefs / acceptQuest / turnInQuest /
// recordQuestEvent / greet) so reconciliation is a no-op — drop the fallbacks later.

export interface QuestSurface {
  readonly questState: QuestState;
  readonly questDefs: readonly QuestDef[];
  acceptQuest(questId: string): void;
  turnInQuest(questId: string): void;
  recordQuestEvent(event: QuestEvent): void;
  greet(trainerId: string): void;
}

export function questView(session: GameSession): QuestSurface {
  const s = session as unknown as Partial<QuestSurface>;
  return {
    get questState(): QuestState {
      return s.questState ?? emptyQuestState();
    },
    get questDefs(): readonly QuestDef[] {
      return s.questDefs ?? [];
    },
    acceptQuest(questId: string): void {
      s.acceptQuest?.call(session, questId);
    },
    turnInQuest(questId: string): void {
      s.turnInQuest?.call(session, questId);
    },
    recordQuestEvent(event: QuestEvent): void {
      s.recordQuestEvent?.call(session, event);
    },
    greet(trainerId: string): void {
      s.greet?.call(session, trainerId);
    },
  };
}

/**
 * Resolve a quest's giver to a warm display name. Side quests name an archetype giver
 * that the session binds to a real regular → use that trainer's username. Main-story
 * quests name a gym-leader story NPC ('npc-leader-*') → use the authored leader name.
 * Falls back to a humanized slug so a row never reads as a raw id.
 */
export function giverDisplayName(session: GameSession, def: QuestDef): string {
  const id = session.questGiverId(def);
  const regular = session.society.find((s) => s.id === id);
  if (regular) return regular.username;
  const leader = GYM_LEADERS.find((l) => l.npcId === id || l.npcId === def.giver);
  if (leader) return leader.name;
  return humanizeId(id);
}

type Tab = 'active' | 'done';

interface RowCell {
  readonly container: Phaser.GameObjects.Container;
  readonly cy: number;
  readonly height: number;
  readonly def: QuestDef;
  readonly progress?: QuestProgress;
}

export class QuestLogScene extends Phaser.Scene {
  private layout!: Layout;
  private session!: GameSession;
  private reduced = false;

  private backdrop!: Phaser.GameObjects.Graphics;
  private heading?: Phaser.GameObjects.Text;
  private subheading?: Phaser.GameObjects.Text;
  private backButton?: Button;
  private tabButtons: { tab: Tab; button: Button }[] = [];

  private list?: ScrollList;
  private listViewH = 0;
  private cells: RowCell[] = [];
  private emptyText?: Phaser.GameObjects.Text;
  private detail?: Phaser.GameObjects.Container;

  private tab: Tab = 'active';
  private cursor = -1;
  private focusRing?: FocusRing;
  private keyHandler?: (e: KeyboardEvent) => void;
  private headerBottom = 0;

  constructor() {
    super('Quests');
  }

  create(): void {
    this.resetState();
    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    this.session = getSession(this);
    this.reduced = isReducedMotion();

    this.backdrop = calmBackdrop(this, this.layout);

    this.heading = this.add
      .text(0, 0, 'Quests', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.subheading = this.add
      .text(0, 0, this.warmSummary(), {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);

    for (const tab of ['active', 'done'] as Tab[]) {
      const button = new Button(this, 0, 0, {
        label: tab === 'active' ? 'Active' : 'Done',
        variant: 'ghost',
        width: 116,
        reducedMotion: this.reduced,
        onClick: () => this.switchTab(tab),
      });
      this.tabButtons.push({ tab, button });
    }

    this.backButton = new Button(this, 0, 0, {
      label: '‹ Town',
      variant: 'ghost',
      width: 110,
      reducedMotion: this.reduced,
      onClick: () => goTo(this, 'Town'),
    });

    this.focusRing = new FocusRing(this, { depth: 90 });

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.setupKeyboard();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    fadeIn(this);
  }

  // ── data ──────────────────────────────────────────────────────────────────────────

  private get view(): QuestSurface {
    return questView(this.session);
  }

  private defById(id: string): QuestDef | undefined {
    return this.view.questDefs.find((d) => d.id === id);
  }

  /** Active quests (started, not turned in) — main pinned above side, in that order. */
  private activeRows(): { def: QuestDef; progress: QuestProgress }[] {
    const rows: { def: QuestDef; progress: QuestProgress }[] = [];
    for (const p of this.view.questState.active) {
      if (p.turnedIn) continue;
      const def = this.defById(p.questId);
      if (def) rows.push({ def, progress: p });
    }
    rows.sort((a, b) => rank(a.def) - rank(b.def));
    return rows;
  }

  /** Finished quests — the warm "Done" shelf. */
  private doneRows(): { def: QuestDef }[] {
    const out: { def: QuestDef }[] = [];
    for (const id of this.view.questState.completed) {
      const def = this.defById(id);
      if (def) out.push({ def });
    }
    out.sort((a, b) => rank(a.def) - rank(b.def));
    return out;
  }

  private warmSummary(): string {
    const active = this.activeRows().length;
    const done = this.doneRows().length;
    if (active === 0 && done === 0) return 'your journal is open — tales will find you as you go';
    const parts: string[] = [];
    if (active > 0) parts.push(`${active} on the go`);
    if (done > 0) parts.push(`${done} finished`);
    return parts.join(' · ');
  }

  // ── keyboard ────────────────────────────────────────────────────────────────────────

  private setupKeyboard(): void {
    this.keyHandler = (e: KeyboardEvent): void => {
      if (typeof document !== 'undefined' && domTextFieldFocused(document.activeElement)) return;
      if (this.detail) {
        if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.closeDetail();
        }
        return;
      }
      if (e.key === 'Tab' || e.key === '[' || e.key === ']') {
        e.preventDefault();
        this.switchTab(this.tab === 'active' ? 'done' : 'active');
        return;
      }
      if (e.key === 'ArrowUp' || e.key === 'w') {
        e.preventDefault();
        this.moveCursor(-1);
      } else if (e.key === 'ArrowDown' || e.key === 's') {
        e.preventDefault();
        this.moveCursor(1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (this.cursor >= 0 && this.cells[this.cursor]) this.openDetail(this.cells[this.cursor]!);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        goTo(this, 'Town');
      }
    };
    if (typeof window !== 'undefined') window.addEventListener('keydown', this.keyHandler);
  }

  private moveCursor(delta: number): void {
    const n = this.cells.length;
    if (n === 0) {
      this.focusRing?.hide();
      return;
    }
    const next = this.cursor < 0 ? (delta > 0 ? 0 : n - 1) : this.cursor + delta;
    this.setCursor(Math.max(0, Math.min(n - 1, next)));
  }

  private setCursor(index: number): void {
    const n = this.cells.length;
    if (n === 0 || !this.list) return;
    this.cursor = Math.max(0, Math.min(n - 1, index));
    const cell = this.cells[this.cursor];
    if (!cell) return;
    const top = cell.cy - cell.height / 2;
    const bottom = cell.cy + cell.height / 2;
    const off = this.list.scrollOffset;
    const pad = SPACING.sm;
    if (top < off) this.list.scrollTo(top - pad);
    else if (bottom > off + this.listViewH) this.list.scrollTo(bottom - this.listViewH + pad);
    const m = cell.container.getWorldTransformMatrix();
    this.focusRing?.show(m.tx, m.ty, cell.container.width, cell.height);
    this.focusRing?.bringToTop();
  }

  // ── lifecycle ─────────────────────────────────────────────────────────────────────

  private resetState(): void {
    this.heading = undefined;
    this.subheading = undefined;
    this.backButton = undefined;
    this.tabButtons = [];
    this.list = undefined;
    this.cells = [];
    this.emptyText = undefined;
    this.detail = undefined;
    this.tab = 'active';
    this.cursor = -1;
    this.focusRing = undefined;
    this.keyHandler = undefined;
  }

  private teardown(): void {
    this.tweens.killAll();
    if (this.keyHandler && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keyHandler);
    }
    this.keyHandler = undefined;
    this.focusRing?.destroy();
    this.focusRing = undefined;
    this.cells = [];
    this.list?.destroy();
    this.detail?.destroy();
    this.emptyText?.destroy();
    for (const { button } of this.tabButtons) button.destroy();
    this.tabButtons = [];
    this.backdrop?.destroy();
    this.heading?.destroy();
    this.subheading?.destroy();
    this.backButton?.destroy();
    this.list = undefined;
    this.detail = undefined;
    this.emptyText = undefined;
  }

  // ── tabs ──────────────────────────────────────────────────────────────────────────

  private switchTab(tab: Tab): void {
    if (tab === this.tab) return;
    this.tab = tab;
    this.cursor = -1;
    this.closeDetail();
    this.subheading?.setText(this.warmSummary());
    this.highlightTabs();
    this.buildList();
    this.focusRing?.hide();
  }

  private highlightTabs(): void {
    for (const { tab, button } of this.tabButtons) button.setAlpha(tab === this.tab ? 1 : 0.55);
  }

  // ── list ──────────────────────────────────────────────────────────────────────────

  private buildList(): void {
    this.list?.destroy();
    this.emptyText?.destroy();
    this.emptyText = undefined;
    this.cells = [];

    const safe = this.layout.safe;
    const top = this.headerBottom;
    const listW = Math.min(safe.width - SPACING.lg * 2, 620);
    const listH = Math.max(160, safe.y + safe.height - top - SPACING.lg);
    const cx = this.layout.centerX;

    this.listViewH = listH;
    const list = new ScrollList(this, cx, top + listH / 2, { width: listW, height: listH });
    this.list = list;

    const rows = this.tab === 'active' ? this.activeRows() : null;
    const done = this.tab === 'done' ? this.doneRows() : null;
    const count = rows?.length ?? done?.length ?? 0;

    if (count === 0) {
      this.emptyText = this.add
        .text(cx, top + listH / 2, this.emptyMessage(), {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.md}px`,
          color: CSS.textMuted,
          align: 'center',
          wordWrap: { width: Math.min(listW - SPACING.lg, 420) },
          lineSpacing: 4,
        })
        .setOrigin(0.5);
      return;
    }

    const rowW = listW - SPACING.md;
    let y = SPACING.sm;
    const add = (def: QuestDef, progress?: QuestProgress): void => {
      const { container, height } = this.buildRow(def, progress, rowW);
      const cy = y + height / 2;
      container.setPosition(0, cy);
      list.content.add(container);
      this.cells.push({ container, cy, height, def, progress });
      y += height + SPACING.md;
    };

    if (rows) for (const r of rows) add(r.def, r.progress);
    if (done) for (const r of done) add(r.def);

    list.setContentHeight(y + SPACING.md);
    list.scrollTo(0);
  }

  private emptyMessage(): string {
    return this.tab === 'active'
      ? 'No quests on the go just yet.\nWander, chat with the regulars, and tales will find you.'
      : 'Your finished tales will gather here.\nThere’s no hurry — take the scenic route.';
  }

  /** Build one quest row container; returns it plus its measured height. */
  private buildRow(
    def: QuestDef,
    progress: QuestProgress | undefined,
    width: number,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.add.container(0, 0);
    const pad = SPACING.md;
    const innerW = width - pad * 2;
    const giver = giverDisplayName(this.session, def);

    let y = pad;
    const title = this.add.text(-width / 2 + pad, y, def.title, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.md}px`,
      color: CSS.text,
      fontStyle: 'bold',
      wordWrap: { width: innerW - 64 },
    });
    // A soft main/side chip, top-right.
    const chip = this.add
      .text(width / 2 - pad, y + 2, def.kind === 'main' ? 'Story' : 'Side', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: def.kind === 'main' ? CSS.accent : CSS.textMuted,
        fontStyle: 'bold',
      })
      .setOrigin(1, 0);
    c.add([title, chip]);
    y += title.height + 2;

    const giverText = this.add.text(-width / 2 + pad, y, `from ${giver}`, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.xs}px`,
      color: CSS.textFaint,
    });
    c.add(giverText);
    y += giverText.height + SPACING.xs;

    const synopsis = this.add.text(-width / 2 + pad, y, def.synopsis, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: CSS.textMuted,
      wordWrap: { width: innerW },
      lineSpacing: 2,
    });
    c.add(synopsis);
    y += synopsis.height + SPACING.sm;

    if (progress) {
      const lines = activeStageLines(
        def,
        progress.stageIndex,
        progress.objectives,
        giver,
        this.lookup(),
      );
      for (const line of lines) {
        const t = this.add.text(-width / 2 + pad + SPACING.sm, y, line, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.sm}px`,
          color: line.startsWith('✓') || line.startsWith('All done') ? CSS.accent : CSS.text,
          wordWrap: { width: innerW - SPACING.sm },
          lineSpacing: 2,
        });
        c.add(t);
        y += t.height + SPACING.xs;
      }
    } else {
      const doneLine = this.add.text(-width / 2 + pad + SPACING.sm, y, '✓ A tale well told', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.accent,
      });
      c.add(doneLine);
      y += doneLine.height + SPACING.xs;
    }

    const height = y + pad;

    // The panel behind the content, drawn first (added to back) at the measured size.
    const panel = new Panel(this, 0, 0, {
      width,
      height,
      fill: COLORS.surface,
      radius: RADIUS.md,
    });
    // Panel origin is centered; shift the top-anchored content up by height/2.
    c.list.forEach((o) => {
      const go = o as Phaser.GameObjects.Components.Transform & Phaser.GameObjects.GameObject;
      if ('y' in go) (go as unknown as { y: number }).y -= height / 2;
    });
    c.addAt(panel, 0);
    c.setSize(width, height);

    if (progress) {
      // Active rows are tappable → show the current stage's opening lines.
      panel.setInteractive(
        new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height),
        Phaser.Geom.Rectangle.Contains,
      );
      panel.input!.cursor = 'pointer';
      panel.on(Phaser.Input.Events.POINTER_UP, () => {
        if (this.list?.wasDragging()) return;
        const cell = this.cells.find((r) => r.container === c);
        if (cell) this.openDetail(cell);
      });
    }

    return { container: c, height };
  }

  private lookup() {
    const index = this.session.bundle.index;
    return {
      speciesName: (id: string) => index.speciesById(id)?.name,
      moveName: (id: string) => index.moveById(id)?.name,
      trainerName: (id: string) => {
        // Objectives may carry a REF ('role:rival', 'archetype:…') — resolve it to the
        // real bound trainer before the roster lookup so the log names a real person.
        const real = this.session.resolveGiver(id);
        return this.session.society.find((s) => s.id === real)?.username;
      },
    };
  }

  // ── detail: the current stage's opening lines ───────────────────────────────────────

  private openDetail(cell: RowCell): void {
    if (!cell.progress) return;
    this.closeDetail();
    const def = cell.def;
    const stageIndex = cell.progress.stageIndex;
    const giver = giverDisplayName(this.session, def);

    let lines: string[];
    if (isReadyToTurnIn(def, stageIndex, cell.progress.turnedIn)) {
      lines = [`It’s all done — ${giver} would love to hear about it. Say hi in town!`];
    } else {
      const stage = def.stages[stageIndex];
      lines = stage ? [...stage.dialogue.onStart] : [def.synopsis];
    }

    const safe = this.layout.safe;
    const w = Math.min(560, safe.width - SPACING.lg);
    const h = 132;

    const overlay = this.add.container(0, 0).setDepth(200);
    const scrim = this.add
      .rectangle(
        this.layout.centerX,
        this.layout.centerY,
        this.layout.width,
        this.layout.height,
        COLORS.scrim,
        0.5,
      )
      .setOrigin(0.5)
      .setInteractive();
    scrim.on(Phaser.Input.Events.POINTER_DOWN, () => this.closeDetail());
    overlay.add(scrim);

    const dialog = new DialogBox(
      this,
      this.layout.centerX,
      safe.y + safe.height - h / 2 - SPACING.md,
      {
        width: w,
        height: h,
        reducedMotion: this.reduced,
        onDone: () => this.closeDetail(),
      },
    );
    dialog.setDepth(201);
    dialog.show(lines, def.title);
    overlay.add(dialog);
    this.detail = overlay;
  }

  private closeDetail(): void {
    this.detail?.destroy();
    this.detail = undefined;
  }

  // ── layout ──────────────────────────────────────────────────────────────────────────

  private relayout(): void {
    redrawBackdrop(this.backdrop, this.layout);
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const top = safe.y + SPACING.md;

    this.subheading?.setText(this.warmSummary());

    const backW = this.backButton?.buttonWidth ?? 110;
    const backH = this.backButton?.buttonHeight ?? 48;
    const headingH = this.heading?.height ?? 24;
    const subH = this.subheading?.height ?? 18;
    const backCx = safe.x + SPACING.md + backW / 2;

    // Row 1: back button (left) + heading (center). Row 2: subtitle. Row 3: tabs.
    const backY = top + backH / 2;
    this.backButton?.setPosition(backCx, backY);
    const headingY = top + headingH / 2;
    this.heading?.setPosition(cx, headingY);
    const subY = Math.max(headingY + headingH / 2, backY + backH / 2) + SPACING.xs + subH / 2;
    this.subheading?.setPosition(cx, subY);

    const tabH = this.tabButtons[0]?.button.buttonHeight ?? 48;
    const tabW = this.tabButtons[0]?.button.buttonWidth ?? 116;
    const tabY = subY + subH / 2 + SPACING.sm + tabH / 2;
    const gap = SPACING.sm;
    const totalTabsW = this.tabButtons.length * tabW + (this.tabButtons.length - 1) * gap;
    let tx = cx - totalTabsW / 2 + tabW / 2;
    for (const { button } of this.tabButtons) {
      button.setPosition(tx, tabY);
      tx += tabW + gap;
    }

    this.headerBottom = tabY + tabH / 2 + SPACING.md;

    this.highlightTabs();
    this.closeDetail();
    this.buildList();
    if (this.cursor >= 0) this.setCursor(this.cursor);
    else this.focusRing?.hide();
  }
}

/** Main story quests sort before side quests; stable within a kind by id. */
function rank(def: QuestDef): number {
  return def.kind === 'main' ? 0 : 1;
}
